import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { isSupportedLanguage, PolicyId } from '@estercare/ester-shared'
import { useGetPolicyDocument } from '../../api/ester-b2b-api/public/policies/policiesQueries'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import { BodyLarge } from '../../components/styles/Typography'
import { theme } from '../../theme/theme'
import { PolicyDocumentBody } from './PolicyDocumentBody'
import { PolicyCard, PolicyCardBody, PolicyCardTop, PolicyPageMain, PolicyPageShell } from './PolicyPageShell'

export const DataPolicyPage = () => {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const { hash } = useLocation()
  const bodyRef = useRef<HTMLDivElement>(null)
  const version = searchParams.get('version') ?? undefined
  const languageParam = searchParams.get('language')
  const language = isSupportedLanguage(languageParam) ? languageParam : i18n.language
  const {
    data: policy,
    isLoading,
    isError,
  } = useGetPolicyDocument({ policyId: PolicyId.DATA_POLICY, language, version })

  useEffect(() => {
    if (language !== i18n.language) void i18n.changeLanguage(language)
  }, [language, i18n])

  useEffect(() => {
    const section = hash.slice(1)
    if (!policy || !/^\d+$/.test(section)) return
    const headings = Array.from(bodyRef.current?.querySelectorAll('h2') ?? [])
    headings.find((heading) => heading.textContent?.startsWith(`${section}.`))?.scrollIntoView()
  }, [policy, hash])

  return (
    <PolicyPageShell>
      <PolicyPageMain>
        <PolicyCard>
          <PolicyCardTop>
            <span>
              {policy && t('common.dataPolicy.versionLine', { version: policy.version, date: policy.effectiveFrom })}
            </span>
          </PolicyCardTop>
          <PolicyCardBody ref={bodyRef}>
            {isLoading && <LoadingSpinner size={40} color={theme.color.plum} />}
            {isError && <ErrorText>{t('common.dataPolicy.loadError')}</ErrorText>}
            {policy && <PolicyDocumentBody markdown={policy.markdown} />}
          </PolicyCardBody>
        </PolicyCard>
      </PolicyPageMain>
    </PolicyPageShell>
  )
}

const ErrorText = styled(BodyLarge)`
  padding-top: ${theme.spacing.mediumLarge}px;
`
